/**
 * cl-core/ad-variant — 광고(파워링크) 유입 판정 (CL fix_4 §2). 순수 TS · 서버·클라이언트 공용.
 *
 * 판정: `utm_source=naver_ad` 이거나 `n_media` · `n_query` 파라미터가 하나라도 있으면 광고 유입.
 * ⚠️ 상세는 ISR(revalidate 3600)이라 서버 렌더는 쿼리를 보지 않는다 — 판정은 클라이언트에서
 *    `location.search` 로 부른다(서버가 searchParams 를 읽으면 라우트가 동적으로 바뀐다).
 * ⚠️ canonical·색인은 변형과 무관하다(이 판정은 표시 위치만 바꾼다).
 */

export type QueryLike =
  | string
  | { get(name: string): string | null; has?(name: string): boolean }
  | Record<string, string | string[] | null | undefined>
  | null
  | undefined;

function readParam(q: QueryLike, name: string): string | null {
  if (q == null) return null;
  if (typeof q === 'string') {
    const s = q.charAt(0) === '?' ? q.slice(1) : q;
    for (const part of s.split('&')) {
      if (!part) continue;
      const eq = part.indexOf('=');
      const rawK = eq >= 0 ? part.slice(0, eq) : part;
      const rawV = eq >= 0 ? part.slice(eq + 1) : '';
      let k = rawK;
      let v = rawV;
      try { k = decodeURIComponent(rawK.replace(/\+/g, ' ')); } catch { /* 원문 유지 */ }
      try { v = decodeURIComponent(rawV.replace(/\+/g, ' ')); } catch { /* 원문 유지 */ }
      if (k === name) return v;
    }
    return null;
  }
  if (typeof (q as { get?: unknown }).get === 'function') {
    return (q as { get(name: string): string | null }).get(name);
  }
  const v = (q as Record<string, string | string[] | null | undefined>)[name];
  if (v == null) return null;
  return Array.isArray(v) ? (v[0] ?? '') : String(v);
}

/** 광고 유입 변형인가. */
export function isAdVariant(q: QueryLike): boolean {
  const utm = readParam(q, 'utm_source');
  if (utm && utm.trim().toLowerCase() === 'naver_ad') return true;
  return readParam(q, 'n_media') != null || readParam(q, 'n_query') != null;
}

/** 광고 변형 모바일 고정 버튼 문구(fix_4 §2). */
export const AD_VARIANT_CTA_LABEL = '분양 정보 안내 신청';

/* ───────── 유입 캡처 계약(fix_7 §1 · 레인 0b) ─────────
 * 쿠키 `kd_entry`(30분, path=/) = URL 인코딩된 JSON {"src":"naver_ad"|"organic"|"cafe"|"kakao","n_media"?,"n_query"?,"utm_source"?,"ts"}
 * 클라이언트 sessionStorage `kd_entry` 에 같은 JSON 미러. 여기서는 «읽기» 만 한다(쓰기는 레인 0b 모듈).
 */
export const KD_ENTRY_KEY = 'kd_entry';

export type EntrySource = 'naver_ad' | 'organic' | 'cafe' | 'kakao';

export interface KdEntry {
  src: EntrySource;
  n_media?: string;
  n_query?: string;
  utm_source?: string;
  ts?: number | string;
}

const ENTRY_SOURCES: readonly string[] = ['naver_ad', 'organic', 'cafe', 'kakao'];

/** 값(인코딩 여부 무관 JSON 문자열) → KdEntry. 형식이 틀리면 null. */
export function parseKdEntry(raw: string | null | undefined): KdEntry | null {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  for (let i = 0; i < 2 && s.charAt(0) !== '{'; i++) {
    try { s = decodeURIComponent(s); } catch { return null; }
  }
  try {
    const o = JSON.parse(s) as Record<string, unknown>;
    if (!o || typeof o !== 'object' || ENTRY_SOURCES.indexOf(String(o.src)) < 0) return null;
    const out: KdEntry = { src: o.src as EntrySource };
    if (o.n_media != null) out.n_media = String(o.n_media);
    if (o.n_query != null) out.n_query = String(o.n_query);
    if (o.utm_source != null) out.utm_source = String(o.utm_source);
    if (typeof o.ts === 'number' || typeof o.ts === 'string') out.ts = o.ts;
    return out;
  } catch {
    return null;
  }
}

/** `document.cookie` 문자열에서 kd_entry 를 읽는다. */
export function kdEntryFromCookie(cookieHeader: string | null | undefined): KdEntry | null {
  const s = String(cookieHeader ?? '');
  for (const part of s.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() === KD_ENTRY_KEY) return parseKdEntry(part.slice(eq + 1).trim());
  }
  return null;
}

/** 광고 변형 판정(통합): 현재 URL 쿼리 «또는» 캡처된 유입(src==='naver_ad'). */
export function isAdVariantWith(q: QueryLike, entry: KdEntry | null | undefined): boolean {
  return isAdVariant(q) || (!!entry && entry.src === 'naver_ad');
}
